// src/support/orgScope.ts
export interface OrgChartRow {
  user_id: number;
  user_email: string;
  is_manager: boolean;
  manager_id?: number;
  level_1?: string;
  level_2?: string;
  [k: string]: any;
}

export interface OrgScope {
  rootEmail: string;
  rootUserId: number | null;
  emails: string[];
  userIds: number[];
  managers: { email: string; userId: number }[];
}

/**
 * Build a direct-report map using manager_id.
 */
export function buildDirectReportsMap(rows: OrgChartRow[]) {
  const byManager = new Map<number, OrgChartRow[]>();
  const byEmail = new Map<string, OrgChartRow>();

  for (const r of rows) {
    if (r?.user_email) byEmail.set(String(r.user_email).toLowerCase(), r);
    const mid = r?.manager_id;
    if (mid != null) {
      const arr = byManager.get(mid) ?? [];
      arr.push(r);
      byManager.set(mid, arr);
    }
  }

  return { byManager, byEmail };
}

export function computeSubtreeScope(rows: OrgChartRow[], rootEmail: string): OrgScope {
  const normalizedRoot = String(rootEmail || '').toLowerCase();
  const { byManager, byEmail } = buildDirectReportsMap(rows);

  const rootRow = byEmail.get(normalizedRoot);
  const rootUserId = rootRow?.user_id ?? null;

  // If root isn't in org chart, fallback to only that email.
  if (!rootUserId) {
    return {
      rootEmail: normalizedRoot,
      rootUserId: null,
      emails: normalizedRoot ? [normalizedRoot] : [],
      userIds: [],
      managers: [],
    };
  }

  const emails: string[] = [];
  const userIds: number[] = [];
  const managers: { email: string; userId: number }[] = [];

  const q: number[] = [rootUserId];
  const visited = new Set<number>();

  while (q.length) {
    const cur = q.shift()!;
    if (visited.has(cur)) continue;
    visited.add(cur);

    const children = byManager.get(cur) ?? [];
    for (const child of children) {
      if (child?.user_id == null) continue;

      userIds.push(child.user_id);
      if (child.user_email) emails.push(String(child.user_email).toLowerCase());
      if (child.is_manager && child.user_email) {
        managers.push({ email: String(child.user_email).toLowerCase(), userId: child.user_id });
      }

      q.push(child.user_id);
    }
  }

  // include root itself in scope
  const scopeEmails = [normalizedRoot, ...emails];
  const scopeIds = [rootUserId, ...userIds];

  // de-dupe
  const uniqEmails = Array.from(new Set(scopeEmails.filter(Boolean)));
  const uniqIds = Array.from(new Set(scopeIds.filter((n) => Number.isFinite(n))));

  return {
    rootEmail: normalizedRoot,
    rootUserId,
    emails: uniqEmails,
    userIds: uniqIds,
    managers: [{ email: normalizedRoot, userId: rootUserId }, ...managers].filter(
      (m, idx, arr) => arr.findIndex((x) => x.email === m.email) === idx
    ),
  };
}

export function getDirectReports(rows: OrgChartRow[], managerEmail: string): OrgChartRow[] {
  const email = String(managerEmail || '').toLowerCase();
  const managerRow = rows.find(r => String(r.user_email).toLowerCase() === email);
  if (!managerRow?.user_id) return [];

  return rows
    .filter(r => r.manager_id === managerRow.user_id)
    .slice()
    .sort((a, b) => String(a.user_email).localeCompare(String(b.user_email)));
}

export function isManagerEmail(rows: OrgChartRow[], email: string | null | undefined): boolean {
  const e = String(email || '').toLowerCase();
  const row = rows.find(r => String(r.user_email).toLowerCase() === e);
  return !!row?.is_manager;
}

/**
 * Return all report emails under managerEmail (recursive), including direct + indirect.
 * Does NOT include the managerEmail itself.
 */
export function getAllReportsEmails(rows: OrgChartRow[], managerEmail: string): string[] {
  const root = String(managerEmail || '').toLowerCase();
  if (!root) return [];

  const out: string[] = [];
  const visited = new Set<string>();
  const queue: string[] = [root];

  visited.add(root);

  while (queue.length) {
    const mgr = queue.shift()!;
    const direct = getDirectReports(rows, mgr);

    for (const r of direct) {
      const e = String((r as any).user_email || '').toLowerCase();
      if (!e) continue;

      out.push(e);

      // continue traversal even if person is not marked manager in data;
      // getDirectReports will return [] anyway.
      if (!visited.has(e)) {
        visited.add(e);
        queue.push(e);
      }
    }
  }

  // de-dupe (keep stable-ish order)
  return Array.from(new Set(out));
}